import { isPresent } from "@apl-digital/utils";
import { isAnyOf } from "@reduxjs/toolkit";
import { deepEqual } from "fast-equals";

import { ProductType } from "@/api/interfaces/productLayouts";
import CartService from "@/api/service/cartService";
import { getUnixDateFromUTC } from "@/base/utils/dateHelpers";
import { clearStorage, setStorage, StorageKey } from "@/constants/storage";
import { hasData } from "@/store/apiRequestStatusMachine";
import type { AppStartListening } from "@/store/listenerMiddleware";

import { isStatus } from "../utils";

import {
  addProductToCart,
  deleteShoppingCart,
  initializeDiningCart,
  refreshCart,
  removeAllProductsFromCart,
  removeCustomerFromCart,
  removeLinesFromCart,
  removeProductFromCart,
  updateCustomerCart,
  updateShoppingCartLanguage,
} from "./actions";
import { isShoppingCartExpired } from "./helper";
import { selectShoppingCartProducts } from "./selectors";
import {
  promoteNewShoppingCart,
  resetShoppingCart,
  selectCurrentTransactionId,
  selectNewShoppingCart,
  selectShoppingCart,
} from "./slice";

export const addShoppingCartListeneres = (
  startListening: AppStartListening,
) => {
  startListening({
    predicate: (_action, currentState, previousState) =>
      !deepEqual(
        previousState.shoppingCart.shoppingCart,
        currentState.shoppingCart.shoppingCart,
      ),
    effect: (_action, { getState }) => {
      const shoppingCart = selectShoppingCart(getState());

      if (hasData(shoppingCart) && !isShoppingCartExpired(shoppingCart.state)) {
        setStorage(StorageKey.CART, JSON.stringify(shoppingCart.state), {
          type: "expiringRecord",
          expiresAt: getUnixDateFromUTC(shoppingCart.state.dttmExpiresUTC),
        });
      } else {
        clearStorage(StorageKey.CART);
      }
    },
  });

  startListening({
    predicate: (_action, currentState, previousState) =>
      !deepEqual(previousState.user.userData, currentState.user.userData) &&
      isPresent(currentState.shoppingCart.shoppingCart),
    effect: async (_action, { getState, dispatch }) => {
      const transactionId = selectCurrentTransactionId(getState());
      const userData = getState().user.userData;

      if (isPresent(userData) && isPresent(transactionId)) {
        await dispatch(
          updateCustomerCart({
            transactionId,
            body: {
              CustomerPersonID: userData.ID,
              LoyaltyProgramMembershipID: 0,
            },
          }),
        );
      } else {
        const shoppingCart = getState().shoppingCart.shoppingCart;

        // The predicate above ensures that shoppingCart is present.
        if (!hasData(shoppingCart)) {
          return;
        }

        await dispatch(
          removeCustomerFromCart({
            transactionId: shoppingCart.state.ID,
          }),
        );
      }
    },
  });

  startListening({
    matcher: isAnyOf(
      addProductToCart.fulfilled,
      addProductToCart.rejected,
      removeLinesFromCart.fulfilled,
      removeProductFromCart.fulfilled,
      removeAllProductsFromCart.fulfilled,
      updateShoppingCartLanguage.fulfilled,
    ),
    effect: async (_action, { dispatch, getState }) => {
      const transactionId = selectCurrentTransactionId(getState());
      const newShoppingCart = selectNewShoppingCart(getState());

      if (isPresent(transactionId) && isStatus(newShoppingCart, "idle")) {
        await dispatch(refreshCart({ transactionId }));
      }
    },
  });

  startListening({
    matcher: isAnyOf(
      removeAllProductsFromCart.rejected,
      removeCustomerFromCart.rejected,
      refreshCart.rejected,
    ),
    effect: async (_action, { dispatch, getState }) => {
      const transactionId = selectCurrentTransactionId(getState());

      dispatch(resetShoppingCart());

      if (isPresent(transactionId)) {
        await dispatch(deleteShoppingCart({ transactionId }));
      }
    },
  });

  startListening({
    predicate: (_action, currentState, previousState) =>
      previousState.shoppingCart.shoppingCartLanguage !==
      currentState.shoppingCart.shoppingCartLanguage,
    effect: async (_action, { getState, dispatch }) => {
      const shoppingCartLanguage = getState().shoppingCart.shoppingCartLanguage;

      if (isPresent(shoppingCartLanguage)) {
        const transactionId = selectCurrentTransactionId(getState());

        setStorage(StorageKey.SHOPPING_CART_LANGUAGE, shoppingCartLanguage);

        if (!isPresent(transactionId)) {
          return;
        }

        await dispatch(
          updateShoppingCartLanguage({
            transactionId,
            body: {
              language: shoppingCartLanguage,
            },
          }),
        );
      } else {
        clearStorage(StorageKey.SHOPPING_CART_LANGUAGE);
      }
    },
  });

  startListening({
    matcher: isAnyOf(initializeDiningCart.fulfilled),
    effect: async (_action, { dispatch, getState }) => {
      const newShoppingCart = selectNewShoppingCart(getState());
      const oldTransactionId = selectCurrentTransactionId(getState());
      const shoppingCartProducts = selectShoppingCartProducts(getState());

      if (!isStatus(newShoppingCart, "succeeded")) {
        throw new Error("New shopping cart is not present");
      }

      const addRequests = shoppingCartProducts
        .filter((product) => product.Product.Type !== ProductType.Surcharge)
        .map((product) => {
          const modifiers = product.ProductModifierChoices?.filter(
            (modifier) => modifier.ProductModifierChoiceID !== 0,
          ).map((modifier) => ({
            ProductModifierID: modifier.ProductModifierID,
            ProductModifierChoiceID: modifier.ProductModifierChoiceID,
            Amount: modifier.Quantity,
            ProductId: modifier.RelatedProductID,
          }));

          const noteToKitchen = product.ProductModifierChoices?.find(
            (modifier) => modifier.ProductModifierChoiceID === 0,
          )?.ProductModifierChoiceDisplayName;

          const addRequest = CartService.buildAddItemRequest({
            productId: product.Product.ID,
            amount: product.Amount,
            modifiers,
            noteToKitchen,
            variableProductID: product.VariableProductID,
          });

          return addRequest;
        });

      if (addRequests.length > 0) {
        await dispatch(
          addProductToCart({
            transactionId: newShoppingCart.state.ID,
            body: CartService.mergeItemRequest(addRequests),
          }),
        );
      }

      await dispatch(refreshCart({ transactionId: newShoppingCart.state.ID }));

      dispatch(promoteNewShoppingCart());

      if (isPresent(oldTransactionId)) {
        await dispatch(deleteShoppingCart({ transactionId: oldTransactionId }));
      }
    },
  });
};
